/**
 * NeedsAttentionStrip — a compact list of "stuff a manager needs to
 * look at right now". Wave A surfaces ONLY signals we can compute
 * from existing data (no new schema):
 *
 *   • Capture rate below goal      → tallyData.at_goal=false + tx>0
 *   • Tasks awaiting verification  → summary.items[].status=completed
 *   • Tasks needing attention      → summary.items[].status=needs_attention
 *   • Tasks needing handoff        → summary.items[].status=needs_handoff
 *   • Crew with help requested     → summary.items[].help_requested=true
 *   • No notes from THIS shift yet → sheet.manager_notes empty AND
 *                                    no last-shift notes either (so we
 *                                    don't yell at a manager whose
 *                                    handoff card already shows context).
 *   • Truck plan not set today     → truck_plan empty / no shipments
 *   • Weekly figures missing       → wtdData.actual_sales falsy AND
 *                                    we're past Monday of the WTD range
 *                                    (suppress weekend pre-entry noise).
 *
 * Empty state: render a small "All clear" card — every signal is
 * green. The point is to be USEFUL not noisy.
 */
import { Warning, ArrowRight, CheckCircle, Envelope, ClipboardText, Note, HandHeart, Truck, ChartLineUp } from "@phosphor-icons/react";

function Row({ Icon, tone, label, count, onClick, testid }) {
  const toneClasses = {
    rose: "bg-gradient-to-r from-rose-50 to-pink-50 text-rose-800 ring-1 ring-rose-200 hover:from-rose-100 hover:to-pink-100",
    amber: "bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-800 ring-1 ring-amber-200 hover:from-amber-100 hover:to-yellow-100",
    slate: "bg-gradient-to-r from-slate-50 to-slate-100 text-slate-700 ring-1 ring-slate-200 hover:from-slate-100 hover:to-slate-200",
    indigo: "bg-gradient-to-r from-violet-50 to-fuchsia-50 text-violet-800 ring-1 ring-violet-200 hover:from-violet-100 hover:to-fuchsia-100",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-2xl text-left transition-all ${toneClasses[tone] || toneClasses.slate}`}
      data-testid={testid}
    >
      <span className="flex items-center gap-2.5 min-w-0">
        <Icon weight="fill" className="w-4 h-4 shrink-0" />
        <span className="text-[13px] font-black leading-snug truncate">
          {label}
        </span>
      </span>
      <span className="flex items-center gap-1.5 shrink-0">
        {count != null && (
          <span className="text-[11px] font-black tabular-nums px-2 py-0.5 rounded-full bg-white/80 shadow-sm">
            {count}
          </span>
        )}
        <ArrowRight weight="bold" className="w-3.5 h-3.5 opacity-60" />
      </span>
    </button>
  );
}

export default function NeedsAttentionStrip({
  tallyData,
  summary,
  notes,
  lastShiftNotes,
  truckPlan,
  wtdData,
  weeklyFiguresStatus,
  onGoToZoneSheet,
  onGoToTruckPlan,
  onGoToWeeklyFigures,
}) {
  const rows = [];
  // 1. Below capture goal (only flag if there's at least one tx so we
  //    don't yell at managers at 9am before any sales).
  if (tallyData && !tallyData.at_goal && (tallyData.store_transactions || 0) > 0) {
    const ratePct = ((tallyData.capture_rate || 0) * 100).toFixed(1);
    rows.push(
      <Row
        key="below_goal"
        Icon={Envelope}
        tone="rose"
        label={`Capture rate below goal — ${ratePct}%`}
        onClick={onGoToZoneSheet}
        testid="attn-below-goal"
      />,
    );
  }
  // 2/3/4/5. Task signals from the coverage-tasks summary endpoint
  //         (the one we just enriched with verified_by_name etc.).
  const items = summary?.items || [];
  const awaiting = items.filter((i) => i.status === "completed").length;
  const needsAttn = items.filter((i) => i.status === "needs_attention").length;
  const needsHandoff = items.filter((i) => i.status === "needs_handoff").length;
  const helpReq = items.filter((i) => i.help_requested && !i.help_accepted_by).length;
  if (awaiting > 0) {
    rows.push(
      <Row
        key="awaiting"
        Icon={ClipboardText}
        tone="amber"
        label={awaiting === 1 ? "1 task awaiting verification" : `${awaiting} tasks awaiting verification`}
        count={awaiting}
        onClick={onGoToZoneSheet}
        testid="attn-awaiting-verify"
      />,
    );
  }
  if (needsAttn > 0) {
    rows.push(
      <Row
        key="needs_attn"
        Icon={Warning}
        tone="rose"
        label={needsAttn === 1 ? "1 task flagged needs attention" : `${needsAttn} tasks flagged needs attention`}
        count={needsAttn}
        onClick={onGoToZoneSheet}
        testid="attn-needs-attention"
      />,
    );
  }
  if (needsHandoff > 0) {
    rows.push(
      <Row
        key="needs_handoff"
        Icon={Warning}
        tone="amber"
        label={needsHandoff === 1 ? "1 task needs a handoff" : `${needsHandoff} tasks need a handoff`}
        count={needsHandoff}
        onClick={onGoToZoneSheet}
        testid="attn-needs-handoff"
      />,
    );
  }
  if (helpReq > 0) {
    rows.push(
      <Row
        key="help_req"
        Icon={HandHeart}
        tone="indigo"
        label={helpReq === 1 ? "1 crew member asking for help" : `${helpReq} crew members asking for help`}
        count={helpReq}
        onClick={onGoToZoneSheet}
        testid="attn-help-requested"
      />,
    );
  }
  // 6. No notes from THIS shift yet — suppressed when there's last-shift
  //    notes context already on the dashboard via ManagerHandoffCard
  //    (fixes the "dashboard says no notes but a note is visible
  //    elsewhere" mismatch — 2026-06-14 user report).
  const hasTodayNotes = !!(notes && notes.trim());
  const hasLastShiftNotes = !!(lastShiftNotes && (lastShiftNotes.manager_notes || "").trim());
  if (!hasTodayNotes && !hasLastShiftNotes) {
    rows.push(
      <Row
        key="no_notes"
        Icon={Note}
        tone="slate"
        label="No notes from this shift yet"
        onClick={onGoToZoneSheet}
        testid="attn-no-notes"
      />,
    );
  }
  // 7. Truck plan missing — flagged if no truck_plan sub-doc or empty
  //    shipments. The TruckPlanCard itself lives further down under
  //    Operations; this row just deep-links to it.
  const truckMissing = !truckPlan
    || !Array.isArray(truckPlan.shipments)
    || truckPlan.shipments.length === 0;
  if (truckMissing) {
    rows.push(
      <Row
        key="truck_missing"
        Icon={Truck}
        tone="amber"
        label="Truck / pallet plan not set"
        onClick={onGoToTruckPlan || onGoToZoneSheet}
        testid="attn-truck-missing"
      />,
    );
  }
  // 8. Weekly figures missing — uses the dedicated `weeklyFiguresStatus`
  //    prop the dashboard now passes in (a {plan_sales, ly_sales} doc
  //    pulled from /api/biz/weekly-ly/{weekStart}). Pre-fix this row
  //    was driven by `wtdData.actual_sales` — a totally different
  //    field that tracks today's running sales, NOT the weekly
  //    Plan/LY entry status. As a result the row showed "weekly
  //    figures missing" even when the manager HAD entered Plan + LY
  //    on Sunday, because actual_sales was still 0. (User report
  //    2026-06-15: "weekly figures already entered but dashboard
  //    still showing Weekly Figures Missing".)
  //
  //    Now: missing iff EITHER plan_sales OR ly_sales is falsy/<=0
  //    in the actual weekly_ly doc. Mirrors the WeeklyFiguresBanner's
  //    own check exactly so the two surfaces can never disagree.
  const weeklyMissing = !!weeklyFiguresStatus
    && (
      !(weeklyFiguresStatus.plan_sales && Number(weeklyFiguresStatus.plan_sales) > 0)
      || !(weeklyFiguresStatus.ly_sales && Number(weeklyFiguresStatus.ly_sales) > 0)
    );
  if (weeklyMissing) {
    rows.push(
      <Row
        key="weekly_missing"
        Icon={ChartLineUp}
        tone="amber"
        label="Weekly figures missing"
        onClick={onGoToWeeklyFigures || onGoToZoneSheet}
        testid="attn-weekly-missing"
      />,
    );
  }
  // Extension point — additional operational alerts (e.g., backstock,
  // reset compliance, etc.) drop in here as new `rows.push(<Row .../>)`.

  return (
    <section
      className="relative rounded-3xl bg-white border border-slate-200/70 shadow-md overflow-hidden ring-1 ring-slate-100"
      data-testid="needs-attention-strip"
    >
      <div className="absolute inset-x-0 top-0 h-1 pointer-events-none" style={{ background: "linear-gradient(90deg, #f43f5e 0%, #f97316 50%, #facc15 100%)" }} />
      <div className="flex items-center justify-between px-4 sm:px-5 pt-4 pb-3 border-b border-slate-100">
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            className="inline-flex items-center justify-center w-9 h-9 rounded-2xl text-white shadow-md shrink-0"
            style={{ background: "linear-gradient(135deg, #f43f5e, #f97316)" }}
          >
            <Warning weight="fill" className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] font-black text-rose-600 leading-none">
              Needs Attention
            </p>
            <p
              className="text-lg font-black text-slate-900 leading-tight mt-1"
              style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
            >
              {rows.length === 0 ? "All clear" : `${rows.length} thing${rows.length === 1 ? "" : "s"} to look at`}
            </p>
          </div>
        </div>
      </div>
      <div className="px-4 sm:px-5 py-4">
        {rows.length === 0 ? (
          <div
            className="flex items-center gap-3 rounded-2xl px-3.5 py-3 bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50 ring-1 ring-emerald-100"
            data-testid="attn-all-clear"
          >
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-2xl text-white shadow-sm shrink-0" style={{ background: "linear-gradient(135deg, #10b981, #14b8a6)" }}>
              <CheckCircle weight="fill" className="w-5 h-5" />
            </span>
            <span className="text-sm font-black text-emerald-800">
              Everything&apos;s tracking. Let&apos;s keep winning.
            </span>
          </div>
        ) : (
          <div className="space-y-2">{rows}</div>
        )}
      </div>
    </section>
  );
}
